import { JobCode, TimeOffReason, ScheduleShift, Employee, ShiftFormData, TimeOffReasonRequest } from '../../types';
import { GeneralSettings, GeneralSettingsRequest } from '../../types/scheduler';
import { getTimeOffReasons as apiGetTimeOffReasons, createTimeOffReason as apiCreateTimeOffReason, updateTimeOffReason as apiUpdateTimeOffReason, deleteTimeOffReason as apiDeleteTimeOffReason, getGeneralSettings as apiGetGeneralSettings, createGeneralSettings as apiCreateGeneralSettings, updateGeneralSettings as apiUpdateGeneralSettings } from '../API/scheduler';

// Application service layer — surfaces API failures instead of fabricating records (DATA-007)
export const schedulerService = {
    // Employees
    getEmployees: async (): Promise<Employee[]> => {
        throw new Error('schedulerService.getEmployees is not implemented against a live API');
    },

    // Shifts
    getShifts: async (_startDate: Date, _endDate: Date): Promise<ScheduleShift[]> => {
        throw new Error('schedulerService.getShifts is not implemented against a live API');
    },

    addShift: async (shift: ShiftFormData): Promise<ScheduleShift> => {
        throw new Error('schedulerService.addShift is not implemented against a live API');
    },

    updateShift: async (shift: ScheduleShift): Promise<ScheduleShift> => {
        throw new Error('schedulerService.updateShift is not implemented against a live API');
    },

    deleteShift: async (_id: string): Promise<boolean> => {
        throw new Error('schedulerService.deleteShift is not implemented against a live API');
    },

    // Business Date Utils
    getBusinessDate: (date: Date = new Date()): Date => {
        // Convert date to business date logic here
        // This is a simplified version - you might need more complex logic
        // based on your specific business rules
        const businessStartHour = 0; // midnight
        const d = new Date(date);
        if (d.getHours() < businessStartHour) {
            d.setDate(d.getDate() - 1);
        }
        return d;
    },

    getTimeSlots: (): string[] => {
        const slots: string[] = [];
        const totalMinutesInDay = 24 * 60;
        const increment = 15; // 15-minute increments

        for (let minutes = 0; minutes < totalMinutesInDay; minutes += increment) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            slots.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
        }

        return slots;
    },
    // Job Codes
    getJobCodes: async (): Promise<JobCode[]> => {
        throw new Error('schedulerService.getJobCodes is not implemented against a live API');
    },

    addJobCode: async (jobCode: Omit<JobCode, 'id'>): Promise<JobCode> => {
        throw new Error('schedulerService.addJobCode is not implemented against a live API');
    },

    updateJobCode: async (jobCode: JobCode): Promise<JobCode> => {
        throw new Error('schedulerService.updateJobCode is not implemented against a live API');
    },

    deleteJobCode: async (_id: string): Promise<boolean> => {
        throw new Error('schedulerService.deleteJobCode is not implemented against a live API');
    },

    // Time Off Reasons
    getTimeOffReasons: async (): Promise<TimeOffReason[]> => {
        try {
            return await apiGetTimeOffReasons();
        } catch (error) {
            console.error('Error fetching time off reasons:', error);
            throw error;
        }
    },

    addTimeOffReason: async (reason: TimeOffReasonRequest): Promise<TimeOffReason> => {
        try {
            return await apiCreateTimeOffReason(reason);
        } catch (error) {
            console.error('Error creating time off reason:', error);
            throw error;
        }
    },

    updateTimeOffReason: async (id: number, reason: TimeOffReasonRequest): Promise<TimeOffReason> => {
        try {
            return await apiUpdateTimeOffReason(id, reason);
        } catch (error) {
            console.error('Error updating time off reason:', error);
            throw error;
        }
    },

    deleteTimeOffReason: async (id: string): Promise<boolean> => {
        try {
            await apiDeleteTimeOffReason(parseInt(id));
            return true;
        } catch (error) {
            console.error('Error deleting time off reason:', error);
            throw error;
        }
    }
    ,
    // Scheduler Settings persistence
    getSettings: async (): Promise<GeneralSettings | null> => {
        try {
            const settings = await apiGetGeneralSettings();
            return settings ?? null;
        } catch (error) {
            console.error('Error fetching general settings:', error);
            return null;
        }
    },

    saveSettings: async (settings: GeneralSettingsRequest, existingSettings?: GeneralSettings | null): Promise<GeneralSettings> => {
        try {
            if (existingSettings && existingSettings.id) {
                // Update existing settings
                return await apiUpdateGeneralSettings(existingSettings.id, settings);
            } else {
                // Create new settings
                return await apiCreateGeneralSettings(settings);
            }
        } catch (error) {
            console.error('Error saving general settings:', error);
            throw error;
        }
    },

    // Permissions (stored as simple object keyed by permission name)
    getPermissions: async (): Promise<any> => {
        const raw = localStorage.getItem('scheduler-permissions');
        if (raw) {
            try {
                return JSON.parse(raw);
            } catch (e) {
                return null;
            }
        }

        // defaults
        return {
            scheduler: false,
            schedulerAllEmployees: false,
            approveRequests: false,
        };
    },

    savePermissions: async (perms: any): Promise<void> => {
        localStorage.setItem('scheduler-permissions', JSON.stringify(perms));
    }
};